import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, TableData } from '@/components';
import {
  showCaughtErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Column } from '@/types/common';
import {
  getNotificationChannels,
  NotificationChannel,
  NotificationChannelSource,
  SOURCE_LABELS,
  testNotificationChannel,
  updateNotificationChannel,
} from '@/services/api/notificationChannels';
import { getUserEmail } from '@/utils';
import { formatDateTime } from '@/utils/format';
import { SettingsTabs } from './components/SettingsTabs';
import {
  NotificationChannelFormValues,
  NotificationChannelModal,
} from './components/NotificationChannelModal';

const sourceToneClass = (source: NotificationChannelSource): string => {
  switch (source) {
    case 'setting':
      return 'bg-emerald-50 text-emerald-700';
    case 'env':
      return 'bg-blue-50 text-blue-700';
    case 'muted':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-red-50 text-red-700';
  }
};

const NotificationChannelsPage: React.FC = () => {
  const [rows, setRows] = useState<NotificationChannel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<NotificationChannel | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [testingKey, setTestingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setRows(await getNotificationChannels());
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal memuat channel');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (values: NotificationChannelFormValues) => {
    if (!editing) return;
    setIsSaving(true);
    try {
      const url = values.webhookURL.trim();
      await updateNotificationChannel(editing.key, {
        channelName: values.channelName.trim() || null,
        // Omitted = keep; '' = clear; else the new URL.
        ...(values.clearWebhook
          ? { webhookURL: '' }
          : url
            ? { webhookURL: url }
            : {}),
        isActive: values.isActive,
        updatedBy: getUserEmail(),
      });
      showSuccessToast('Berhasil', `Channel ${editing.label} disimpan`);
      setEditing(null);
      await load();
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menyimpan channel');
    } finally {
      setIsSaving(false);
    }
  };

  const runTest = async (row: NotificationChannel) => {
    setTestingKey(row.key);
    try {
      const result = await testNotificationChannel(row.key, getUserEmail());
      showSuccessToast(
        'Terkirim',
        `Pesan tes ${row.label} terkirim (${SOURCE_LABELS[result.source]}). Cek channel-nya.`
      );
    } catch (error) {
      showCaughtErrorToast(error, 'Pesan tes gagal');
    } finally {
      setTestingKey(null);
    }
  };

  const columns = useMemo<Column<NotificationChannel>[]>(
    () => [
      {
        key: 'label',
        title: 'Keluarga alert',
        render: (_value, record) => (
          <div className="max-w-md">
            <p className="font-medium text-gray-900">{record.label}</p>
            <p className="text-[12px] text-gray-500">{record.description}</p>
          </div>
        ),
      },
      {
        key: 'channelName',
        title: 'Channel',
        render: (value) => value || <span className="text-gray-400">-</span>,
      },
      {
        key: 'webhookURLMasked',
        title: 'Webhook',
        render: (value) =>
          value ? (
            <span className="font-mono text-[12px] text-gray-700">{value}</span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        key: 'source',
        title: 'Terkirim ke',
        render: (value: NotificationChannelSource) => (
          <span
            className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${sourceToneClass(
              value
            )}`}
          >
            {SOURCE_LABELS[value]}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        title: 'Diubah',
        render: (_value, record) =>
          record.updatedAt ? (
            <div>
              <p>{formatDateTime(record.updatedAt)}</p>
              <p className="text-[12px] text-gray-500">{record.updatedBy}</p>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_value, record) => (
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="small"
              onClick={() => runTest(record)}
              disabled={testingKey === record.key || record.source === 'none'}
            >
              <i className="ri-send-plane-line text-[12px] mr-1"></i>
              {testingKey === record.key ? 'Mengirim…' : 'Tes kirim'}
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setEditing(record)}
            >
              <i className="ri-pencil-line text-[12px] mr-1"></i>
              Ubah
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testingKey]
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
          Settings
        </h1>
        <p className="text-[14px] text-gray-600">
          Ke channel Slack mana tiap keluarga alert dikirim. Tanpa setting,
          alert memakai webhook dari env server.
        </p>
      </div>
      <SettingsTabs active="slack-channels" />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <TableData
          data={rows}
          columns={columns}
          loading={isLoading}
          size="small"
        />
      </div>

      <NotificationChannelModal
        channel={editing}
        onClose={() => setEditing(null)}
        onSubmit={submit}
        isSaving={isSaving}
      />
    </div>
  );
};

export default NotificationChannelsPage;
